import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FiPackage, FiPlus, FiTruck
} from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import LeftMenu from '../components/LeftMenu';
import withAuth from '../common/auth';
import axios from 'axios';
import { BASE_URL, POST_OPENING_STOCK, GET_ITEM_MASTER } from '../utlilities/config';
import AddItemModal from '../components/AddItemModal';

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '40px', // reduce overall control height
    height: '40px',    // explicitly set height
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px', // reduce internal padding for smaller height
    height: '40px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '40px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Normalize product data from API
const normalizeProductData = (items = []) =>
  items.map((item) => {
    const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? null;
    const itemName = item.Item_Name ?? item.ItemName ?? '';
    const packageValue = item.package ?? item.Package ?? '';
    const unitValue = item.unit ?? item.Unit ?? '';

    return {
      ...item,
      id: item.id ?? itemNo,
      Item_No: itemNo,
      Item_Name: itemName,
      ItemName: item.ItemName ?? itemName,
      package: packageValue,
      Package: item.Package ?? packageValue,
      unit: unitValue,
      Unit: item.Unit ?? unitValue,
      TaxCode: item.TaxCode ?? item.taxCode ?? '',
      Rate: item.Rate ?? item.rate ?? 0,
      MRP: item.MRP ?? item.mrp ?? 0,
    };
  });

const parsePackingQuantity = (value, fallback = 1) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'number') {
    return value > 0 ? value : fallback;
  }
  const numeric = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
  return Number.isNaN(numeric) || numeric <= 0 ? fallback : numeric;
};

// Helper function to get package size from product
const getPackageSize = (product) =>
  parsePackingQuantity(product.packing, 1);

// Helper function to get total units (qty * packing)
const getTotalUnits = (product) => {
  const qty = parseFloat(product.qty) || 0;
  const packageSize = getPackageSize(product);
  return qty * packageSize;
};

// Helper function to get unit rate (rate / packing)
const getUnitRate = (product) => {
  const rate = parseFloat(product.rate) || 0;
  const packageSize = getPackageSize(product);
  return packageSize ? rate / packageSize : rate;
};

const OpeningStockEntry = () => {
  const [loading, setLoading] = useState(false);
  const [entryNumber, setEntryNumber] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [note, setNote] = useState('');
  
  // Product specific states
  const [products, setProducts] = useState([{ 
    product: null, 
    batch: '', 
    expiry: '',
    expiryDisplay: '',
    packing: '',
    qty: 1, 
    rate: 0, 
    mrp: 0,
    discount: '',
    discountAmount: 0,
    sgst: 0,
    cgst: 0,
    gstRate: 0,
    tax: 0,
    totalAmount: 0,
    id: null, 
    name: ""
  }]);
  
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [productsMaster, setProductsMaster] = useState([]);
  const productSearchDebounceRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  const token = localStorage.getItem('accessToken');
  const finac_year = localStorage.getItem('finac_year');

  // Refs
  const noteRef = useRef(null);
  const productRefs = useRef([]);
  const batchRefs = useRef([]);
  const expiryRefs = useRef([]);
  const packingRefs = useRef([]);
  const mrpRefs = useRef([]);
  const qtyRefs = useRef([]);
  const rateRefs = useRef([]);
  const discountRefs = useRef([]);
  const taxRefs = useRef([]);
  const submitbtnRef = useRef(null);

  // Fetch products function
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'PageNo': 1,
          'PageSize': 1000,
          'FilterText': '',
          'FilterColumn': '0',
          'FilterValue': '0',
        },
      });

      if (response.status === 200) {
        const masterData = response.data?.data?.masterData || [];
        const pharmacyItems = masterData.filter(
          (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
        );
        setProductsMaster(normalizeProductData(pharmacyItems));
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Could not load products', {
        autoClose: 1500,
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load - fetch first 1000 products
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Load products from API based on search text (for AsyncSelect)
  const loadProductOptions = (inputValue, callback) => {
    // Clear previous debounce timeout
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }

    // If input is less than 2 characters, return initial products
    if (!inputValue || inputValue.length < 2) {
      const options = (productsMaster || []).map((product) => ({
        value: product.id,
        label: product.Item_Name ?? product.ItemName ?? product.name ?? '',
        genericName: product.Chemical ?? product.chemical ?? '',
        manufacturer: product.Company ?? product.company ?? '',
        packing: product.Package ?? product.package ?? '',
        ...product,
      }));
      callback(options);
      return;
    }

    // Debounce API call
    productSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': inputValue,
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          const normalizedProducts = normalizeProductData(pharmacyItems);
          
          const options = normalizedProducts.map((product) => ({
            value: product.id,
            label: product.Item_Name ?? product.ItemName ?? product.name ?? '',
            genericName: product.Chemical ?? product.chemical ?? '',
            manufacturer: product.Company ?? product.company ?? '',
            packing: product.Package ?? product.package ?? '',
            ...product,
          }));
          
          callback(options);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        callback([]);
      }
    }, 300); // 300ms debounce
  };

  // Get selected product option for AsyncSelect value prop
  const getSelectedProductOption = (productId, productOption = null) => {
    if (!productId) return null;
    
    // First try to find in productsMaster
    let product = productsMaster.find(p => p.id === productId);
    
    // If not found and productOption is provided, use it
    if (!product && productOption) {
      product = productOption;
    }
    
    if (!product) return null;
    
    return {
      value: product.id,
      label: product.Item_Name ?? product.ItemName ?? product.name ?? '',
      genericName: product.Chemical ?? product.chemical ?? '',
      manufacturer: product.Company ?? product.company ?? '',
      packing: product.Package ?? product.package ?? '',
      ...product,
    };
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (productSearchDebounceRef.current) {
        clearTimeout(productSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  // Format date to ISO format (YYYY-MM-DD) in local timezone
  const formatDateString = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    // Use local timezone instead of UTC to preserve IST date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const now = new Date();
    
    // Generate entry number
    setEntryNumber(`OS${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}001`);
    
    // Set entry date in ISO format (YYYY-MM-DD)
    const today = formatDateString(new Date());
    if (today) {
      setEntryDate(today);
    }
  }, []);

  // Calculate discount amount based on discount string
  const calculateDiscountAmount = (amount, discountStr) => {
    if (!discountStr) return 0;
    
    const discountTrimmed = discountStr.toString().trim();
    
    if (discountTrimmed.endsWith('p') || discountTrimmed.endsWith('%')) {
      // Percentage discount
      const percentage = parseFloat(discountTrimmed.slice(0, -1));
      if (!isNaN(percentage)) {
        return (amount * percentage) / 100;
      }
    } else {
      // Fixed amount discount
      const fixedAmount = parseFloat(discountTrimmed);
      if (!isNaN(fixedAmount)) {
        return Math.min(fixedAmount, amount);
      }
    }
    
    return 0;
  };

  // Handle quantity changes
  const handleQtyChange = (index, newQty) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        const qty = parseFloat(newQty || 0);
        const rate = parseFloat(product.rate || 0);
        const amount = qty * rate;
        const discountAmount = calculateDiscountAmount(amount, product.discount);
        const discountedAmount = amount - discountAmount;
        const tax = (discountedAmount * (product.gstRate / 2)) / 100;
        const sgst = tax;
        const cgst = tax;
        const totalAmount = discountedAmount + sgst + cgst;
        
        return {
          ...product,
          qty: newQty,
          amount: amount.toFixed(2),
          discountAmount: discountAmount.toFixed(2),
          tax: tax.toFixed(2),
          sgst: sgst.toFixed(2),
          cgst: cgst.toFixed(2),
          totalAmount: totalAmount.toFixed(2)
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle rate changes
  const handleRateChange = (index, newRate) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        const qty = parseFloat(product.qty || 1);
        const rate = parseFloat(newRate || 0);
        const amount = qty * rate;
        const discountAmount = calculateDiscountAmount(amount, product.discount);
        const discountedAmount = amount - discountAmount;
        const tax = (discountedAmount * (product.gstRate / 2)) / 100;
        const sgst = tax;
        const cgst = tax;
        const totalAmount = discountedAmount + sgst + cgst;
        
        return {
          ...product,
          rate: newRate,
          amount: amount.toFixed(2),
          discountAmount: discountAmount.toFixed(2),
          tax: tax.toFixed(2),
          sgst: sgst.toFixed(2),
          cgst: cgst.toFixed(2),
          totalAmount: totalAmount.toFixed(2)
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle discount changes
  const handleDiscountChange = (index, newDiscount) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        const qty = parseFloat(product.qty || 1);
        const rate = parseFloat(product.rate || 0);
        const amount = qty * rate;
        const discountAmount = calculateDiscountAmount(amount, newDiscount);
        const discountedAmount = amount - discountAmount;
        const tax = (discountedAmount * (product.gstRate / 2)) / 100;
        const sgst = tax;
        const cgst = tax;
        const totalAmount = discountedAmount + sgst + cgst;
        
        return {
          ...product,
          discount: newDiscount,
          discountAmount: discountAmount.toFixed(2),
          tax: tax.toFixed(2),
          sgst: sgst.toFixed(2),
          cgst: cgst.toFixed(2),
          totalAmount: totalAmount.toFixed(2)
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle tax/GST rate changes
  const handleTaxChange = (index, newTaxRate) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        const qty = parseFloat(product.qty || 1);
        const rate = parseFloat(product.rate || 0);
        const amount = qty * rate;
        const discountAmount = calculateDiscountAmount(amount, product.discount);
        const discountedAmount = amount - discountAmount;
        const tax = (discountedAmount * (newTaxRate / 2)) / 100;
        const sgst = tax;
        const cgst = tax;
        const totalAmount = discountedAmount + sgst + cgst;
        
        return {
          ...product,
          gstRate: newTaxRate,
          tax: tax.toFixed(2),
          sgst: sgst.toFixed(2),
          cgst: cgst.toFixed(2),
          totalAmount: totalAmount.toFixed(2)
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle packing changes
  const handlePackingChange = (index, newPacking) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        return {
          ...product,
          packing: newPacking
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle batch number changes
  const handleBatchChange = (index, newBatch) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        return {
          ...product,
          batch: newBatch
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle expiry date input with MM/YY format
  const handleExpiryInputChange = (index, value) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    
    let formattedValue = '';
    
    if (numericValue.length >= 1) {
      formattedValue = numericValue[0];
    }
    if (numericValue.length >= 2) {
      formattedValue = numericValue[0] + numericValue[1];
    }
    if (numericValue.length >= 3) {
      formattedValue = numericValue[0] + numericValue[1] + '/' + numericValue[2];
    }
    if (numericValue.length >= 4) {
      formattedValue = numericValue[0] + numericValue[1] + '/' + numericValue[2] + numericValue[3];
    }
    
    // Convert to full date format for storage (assuming 20xx for years)
    let fullDate = '';
    if (numericValue.length === 4) {
      const month = numericValue.substring(0, 2);
      const year = '20' + numericValue.substring(2, 4);
      fullDate = `${year}-${month}-01`;
    }
    
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        return {
          ...product,
          expiry: fullDate,
          expiryDisplay: formattedValue
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle MRP changes
  const handleMRPChange = (index, newMRP) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        return {
          ...product,
          mrp: newMRP
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle batch input key navigation
  const handleBatchKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (expiryRefs.current[index]) {
        expiryRefs.current[index].focus();
        expiryRefs.current[index].select();
      }
    }
  };

  // Handle expiry input key navigation
  const handleExpiryKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const expiryDisplay = (products[index]?.expiryDisplay || '').trim();
      const numericValue = expiryDisplay.replace(/\D/g, '');

      if (numericValue.length === 4) {
        const month = parseInt(numericValue.substring(0, 2), 10);
        const year = 2000 + parseInt(numericValue.substring(2, 4), 10);

        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();

        const isValidMonth = month >= 1 && month <= 12;
        const isPastMonth = year < currentYear || (year === currentYear && month < currentMonth);

        if (!isValidMonth || isPastMonth) {
          toast.error('Expiry must be current month or a future month.', { autoClose: 1500 });
          setProducts((prev) =>
            prev.map((product, i) =>
              i === index ? { ...product, expiry: '', expiryDisplay: '' } : product
            )
          );
          if (expiryRefs.current[index]) {
            expiryRefs.current[index].focus();
            expiryRefs.current[index].select();
          }
          return;
        }
      }

      if (packingRefs.current[index]) {
        packingRefs.current[index].focus();
        packingRefs.current[index].select();
      }
    }
  };

  // Handle packing input key navigation
  const handlePackingKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (mrpRefs.current[index]) {
        mrpRefs.current[index].focus();
        mrpRefs.current[index].select();
      }
    }
  };

  // Handle MRP input key navigation
  const handleMRPKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (qtyRefs.current[index]) {
        qtyRefs.current[index].focus();
        qtyRefs.current[index].select();
      }
    }
  };

  // Handle quantity input key navigation
  const handleQtyKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (rateRefs.current[index]) {
        rateRefs.current[index].focus();
        rateRefs.current[index].select();
      }
    }
  };

  // Handle rate input key navigation
  const handleRateKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (discountRefs.current[index]) {
        discountRefs.current[index].focus();
        discountRefs.current[index].select();
      }
    }
  };

  // Handle discount input key navigation
  const handleDiscountKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (taxRefs.current[index]) {
        taxRefs.current[index].focus();
        taxRefs.current[index].select();
      }
    }
  };

  // Handle tax input key navigation
  const handleTaxKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = index + 1;
      if (nextIndex < products.length && productRefs.current[nextIndex]) {
        productRefs.current[nextIndex].focus();
      } else {
        if (index === products.length - 1) {
          addProductRow();
          setTimeout(() => {
            if (productRefs.current[nextIndex]) {
              productRefs.current[nextIndex].focus();
            }
          }, 100);
        } else {
          submitbtnRef.current?.focus();
        }
      }
    }
  };

  // Handle product selection
  const handleProductChange = (index, selectedOption) => {
    if (!selectedOption) {
      const newProducts = products.map((prod, i) => {
        if (i === index) {
          return {
            product: null,
            batch: '',
            expiry: '',
            expiryDisplay: '',
            packing: '',
            qty: 1,
            rate: 0,
            mrp: 0,
            discount: '',
            discountAmount: 0,
            sgst: 0,
            cgst: 0,
            gstRate: 0,
            tax: 0,
            totalAmount: 0,
            id: null,
            name: ""
          };
        }
        return prod;
      });
      setProducts(newProducts);
      return;
    }

    // Use selectedOption directly (it contains all product data from AsyncSelect)
    const selectedProduct = selectedOption;
    
    // Add to productsMaster if not already present (for getSelectedProductOption to work)
    if (!productsMaster.find(p => p.id === selectedProduct.id)) {
      setProductsMaster(prev => [...prev, selectedProduct]);
    }

    // Parse packing quantity to get package size
    const packageSize = parsePackingQuantity(
      selectedProduct.Package ?? selectedProduct.package ?? 1,
      1
    );
    
    const gstRateValue = parseFloat(
      selectedProduct.TaxCode ?? selectedProduct.taxCode ?? 0
    );
    const rateValue = selectedProduct.Rate ?? 0;
    const packageMrpValue = selectedProduct.MRP ?? 0;
    
    // Calculate unit MRP (MRP per unit = package MRP / package size)
    const unitMrpValue = packageSize ? packageMrpValue / packageSize : packageMrpValue;

    const newProducts = products.map((prod, i) => {
      if (i === index) {
        const qty = parseFloat(prod.qty || 1);
        const rate = parseFloat(rateValue || 0);
        const amount = qty * rate;
        const discountAmount = calculateDiscountAmount(amount, prod.discount);
        const discountedAmount = amount - discountAmount;
        const tax = (discountedAmount * (gstRateValue / 2)) / 100;
        const sgst = tax;
        const cgst = tax;
        const totalAmount = discountedAmount + sgst + cgst;

        return {
          ...prod,
          id: selectedProduct.id,
          product: selectedOption,
          name: selectedProduct.Item_Name ?? selectedOption.label,
          packing: selectedProduct.Package ?? selectedProduct.package ?? '',
          unit: selectedProduct.Unit ?? selectedProduct.unit ?? '',
          gstRate: gstRateValue,
          taxCode: selectedProduct.TaxCode ?? selectedProduct.taxCode ?? '',
          rate: rateValue,
          mrp: Number.isFinite(unitMrpValue) ? Number(unitMrpValue.toFixed(2)) : 0,
          amount: amount.toFixed(2),
          discountAmount: discountAmount.toFixed(2),
          tax: tax.toFixed(2),
          sgst: sgst.toFixed(2),
          cgst: cgst.toFixed(2),
          totalAmount: totalAmount.toFixed(2)
        };
      }
      return prod;
    });

    setProducts(newProducts);

    setTimeout(() => {
      if (batchRefs.current[index]) {
        batchRefs.current[index].focus();
        batchRefs.current[index].select();
      }
    }, 100);
  };

  const addProductRow = () => {
    setProducts(prevProducts => [...prevProducts, { 
      product: null, 
      batch: '', 
      expiry: '',
      expiryDisplay: '',
      packing: '',
      qty: 1, 
      rate: 0, 
      mrp: 0,
      amount: 0,
      discount: '',
      discountAmount: 0,
      sgst: 0,
      cgst: 0,
      gstRate: 0,
      tax: 0,
      totalAmount: 0,
      id: null, 
      name: ""
    }]);

    // Expand refs arrays
    setTimeout(() => {
      const newLength = products.length + 1;
      if (productRefs.current.length < newLength) {
        productRefs.current = [...productRefs.current, null];
      }
      if (batchRefs.current.length < newLength) {
        batchRefs.current = [...batchRefs.current, null];
      }
      if (expiryRefs.current.length < newLength) {
        expiryRefs.current = [...expiryRefs.current, null];
      }
      if (packingRefs.current.length < newLength) {
        packingRefs.current = [...packingRefs.current, null];
      }
      if (mrpRefs.current.length < newLength) {
        mrpRefs.current = [...mrpRefs.current, null];
      }
      if (qtyRefs.current.length < newLength) {
        qtyRefs.current = [...qtyRefs.current, null];
      }
      if (rateRefs.current.length < newLength) {
        rateRefs.current = [...rateRefs.current, null];
      }
      if (discountRefs.current.length < newLength) {
        discountRefs.current = [...discountRefs.current, null];
      }
      if (taxRefs.current.length < newLength) {
        taxRefs.current = [...taxRefs.current, null];
      }
    }, 50);
  };

  const handleDeleteRow = (index) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts);
  };

  const customProductOptionLabel = ({ label, genericName, manufacturer, packing }) => (
    <div className="flex flex-col">
      <span className="font-bold text-black text-sm">{label}</span>
    </div>
  );

  const resetForm = () => {
    const now = new Date();
    
    // Generate new entry number
    const newEntryNumber = `OS${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}001`;
    setEntryNumber(newEntryNumber);
    
    // Reset entry date to today
    const today = formatDateString(new Date());
    if (today) {
      setEntryDate(today);
    }
    
    // Clear note
    setNote('');
    
    // Reset products to initial state (one empty product)
    setProducts([{ 
      product: null, 
      batch: '', 
      expiry: '',
      expiryDisplay: '',
      packing: '',
      qty: 1, 
      rate: 0, 
      mrp: 0,
      discount: '',
      discountAmount: 0,
      sgst: 0,
      cgst: 0,
      gstRate: 0,
      tax: 0,
      totalAmount: 0,
      id: null, 
      name: ""
    }]);
    
    // Reset other states
    setSelectedIndex(null);
    setIsButtonDisabled(false);
    
    // Clear refs arrays
    productRefs.current = [];
    batchRefs.current = [];
    expiryRefs.current = [];
    packingRefs.current = [];
    mrpRefs.current = [];
    qtyRefs.current = [];
    rateRefs.current = [];
    discountRefs.current = [];
    taxRefs.current = [];
    
    // Focus on note field after reset
    setTimeout(() => {
      if (noteRef.current) {
        noteRef.current.focus();
      }
    }, 100);
    setLoading(false);
    
    toast.success('Form reset successfully', { autoClose: 1000 });
  };

  const handleSave = async () => {
    setIsButtonDisabled(true);
    setLoading(true);

    const validProducts = products.filter(product => product.id && product.name);
    
    if(validProducts.length === 0){
      toast.error('Please select products.', { autoClose: 1000 });
      setLoading(false);
      setIsButtonDisabled(false);
      if (productRefs.current[0]) {
        productRefs.current[0].focus();
      }
      return;
    }

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const stopWithFocus = (toastFn, ref) => {
      toastFn();
      if (ref) {
        ref.focus();
        ref.select?.();
      }
      setLoading(false);
      setIsButtonDisabled(false);
    };

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (!product.id || !product.name) continue;

      if (!(product.batch || '').trim()) {
        stopWithFocus(
          () => toast.error('Please enter batch number for each item.', { autoClose: 1500 }),
          batchRefs.current[i]
        );
        return;
      }

      const expiryDisplay = (product.expiryDisplay || '').trim();
      const numericValue = expiryDisplay.replace(/\D/g, '');
      let month;
      let year;

      if (numericValue.length === 4) {
        month = parseInt(numericValue.substring(0, 2), 10);
        year = 2000 + parseInt(numericValue.substring(2, 4), 10);
      } else if (product.expiry) {
        const d = new Date(product.expiry);
        if (!Number.isNaN(d.getTime())) {
          month = d.getMonth() + 1;
          year = d.getFullYear();
        }
      }

      if (numericValue.length > 0 && numericValue.length < 4) {
        stopWithFocus(
          () => toast.error('Please enter a complete expiry date (MM/YY).', { autoClose: 1500 }),
          expiryRefs.current[i]
        );
        return;
      }

      if (month === undefined || year === undefined) {
        stopWithFocus(
          () => toast.error('Please enter expiry date for each item.', { autoClose: 1500 }),
          expiryRefs.current[i]
        );
        return;
      }

      const isValidMonth = month >= 1 && month <= 12;
      const isPastMonth = year < currentYear || (year === currentYear && month < currentMonth);

      if (!isValidMonth || isPastMonth) {
        stopWithFocus(
          () => toast.error('Expiry must be current month or a future month.', { autoClose: 1500 }),
          expiryRefs.current[i]
        );
        return;
      }

      const totalUnits = getTotalUnits(product);
      if (totalUnits <= 0) {
        stopWithFocus(
          () => toast.error('Quantity must be greater than 0 for each item.', { autoClose: 1500 }),
          qtyRefs.current[i]
        );
        return;
      }

      const mrp = parseFloat(product.mrp);
      if (product.mrp === '' || product.mrp == null || Number.isNaN(mrp) || mrp < 0) {
        stopWithFocus(
          () => toast.error('Please enter MRP (0 or higher) for each item.', { autoClose: 1500 }),
          mrpRefs.current[i]
        );
        return;
      }
    }

    // Format entry date to ISO format (YYYY-MM-DD)
    const entryDateISO = formatDateString(entryDate) || formatDateString(new Date());

    // Parse decimal helper
    const parseDecimal = (value) => parseFloat(parseFloat(value || 0).toFixed(2));

    // Build items array
    const items = validProducts.map((product) => {
      // Calculate package size, total units, and unit rate
      const packageSize = getPackageSize(product);
      const totalUnits = getTotalUnits(product); // packing * qty
      const unitRate = getUnitRate(product); // rate / packing
      
      // Calculate total tax amount (SGST + CGST)
      const sgst = parseFloat(product.sgst || 0);
      const cgst = parseFloat(product.cgst || 0);
      const totalTax = sgst + cgst;
      
      // Use product.amount (amount before discount) which is already calculated as qty * rate
      // This will match: totalUnits * unitRate = (packing * qty) * (rate / packing) = qty * rate
      const amount = parseFloat(product.amount || 0);
      
      return {
        ItemNo: product.id || 0,
        ItemName: product.name || '',
        BatchNo: product.batch || '',
        ExpiryDate: formatDateString(product.expiry) || '',
        Packing: String(packageSize),
        Mrp: parseDecimal(product.mrp),
        Qty: parseDecimal(totalUnits),
        Rate: parseDecimal(unitRate),
        Amount: parseDecimal(amount),
        Discount: parseDecimal(product.discountAmount),
        Taxcode: product.gstRate ? String(product.gstRate) : '0',
        TaxAmount: parseDecimal(totalTax),
        Total: parseDecimal(product.totalAmount),
        DepartmentID: localStorage.getItem('department_id') || '',
      };
    });

    // Build payload
    const payload = {
      Sequence: 0,
      InvNo: 0,
      EntryDate: entryDateISO,
      Notes: note || '',
      Items: items
    };

    console.log('Opening Stock Payload:', payload);

    try {
      const response = await axios.post(`${BASE_URL}${POST_OPENING_STOCK}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Opening Stock Entry created successfully.', { autoClose: 1000 });
        setLoading(false);
        resetForm();
      } else {
        throw new Error('Failed to save opening stock entry');
      }
    } catch (error) {
      console.error('Failed to save opening stock entry:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Messages ||
        'An error occurred while saving the opening stock entry.';
      toast.error(errorMessage, { autoClose: 1500 });
      setLoading(false);
      setIsButtonDisabled(false);
    }
  };

  const handleNewItem = () => {
    setShowModal(true);
  };


  const handleItemSaved = () => {
    // Refresh products list after saving new item
    // This will be called by the modal after successful save
    fetchProducts();
  };

  return (
    <div className='flex h-full bg-background'>
      {/* <LeftMenu /> */}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
          
      {loading && <Spinner />}

      <div className='flex-1 flex h-full flex-col overflow-auto hide-scrollbar'>

        <div className="bg-white p-3 mb-3">
          <div>
            <h1 className="text-lg font-bold text-black-800 flex items-center">
              Opening Stock Entry
            </h1>
          </div>
        </div>

        <div className="bg-white ml-5 mr-5 rounded-lg shadow-sm p-3 mb-3">
          <div className='w-full flex flex-row gap-3'>
            <div className='w-full flex p-3 bg-gray-50 rounded-lg border border-gray-200 flex-col gap-1'>
              {/* Entry Info Section */}
              <div className="flex items-center gap-2 pb-2 border-b border-black border-1">
                <FiPackage className="text-indigo-600" size={16} />
                <h3 className="font-semibold text-black-800 text-sm">Entry Information</h3>
              </div>
              
              <div className='w-full mt-2'>
                <div className='grid grid-cols-12 gap-3'>

                  {/* Entry Date */}
                  <div className='col-span-3'>
                    <label className="block text-xs font-medium text-black-700 mb-1">
                      Entry Date
                    </label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-black border-1 font-medium bg-gray-100 rounded-md text-xs"
                      value={entryDate}
                      readOnly
                    />
                  </div>

                  {/* Note */}
                  <div className='col-span-6'>
                    <label className="block text-xs font-medium text-black-700 mb-1">
                      Note
                    </label>
                    <input
                      type="text"
                      className="w-full p-2.5 border border-black border-1 font-medium bg-white rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-xs"
                      ref={noteRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (productRefs.current[0]) {
                            productRefs.current[0].focus();
                          }
                        }
                      }}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter any additional notes..."
                    />
                  </div>
                  
                </div>
              </div>
            </div>
            
          </div>

          <div className="mt-3">
            <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-300">
              <h3 className="font-semibold text-black-800 text-sm">Product Selection</h3>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  className="py-1.5 px-4 bg-indigo-600 text-white font-medium text-sm rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                  onClick={() => {
                    handleNewItem();
                  }}
                >
                  New Item +
                </button>
                <button 
                  type="button"
                  className="p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                  onClick={addProductRow}
                  title="Add product"
                >
                  <FiPlus size={14} />
                </button>
              </div>
            </div>
                      
            <div className="overflow-x-auto rounded-lg border border-gray-400 pb-5">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">No.</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Product Name</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Batch No</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Expiry</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Packing</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">MRP</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Qty</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Rate</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Discount</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Tax%</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Total</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.length > 0 ? (
                    products.map((product, index) => (
                      <tr 
                        key={index} 
                        className={`${
                          selectedIndex === index 
                            ? "bg-indigo-50" 
                            : "hover:bg-gray-50"
                        } transition-colors`}
                        onClick={() => setSelectedIndex(index)}
                      >                
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-black-500">
                          {index + 1}
                        </td>
                        
                        <td className="px-3 py-2" style={{ minWidth: '300px' }}>
                          <AsyncSelect
                            loadOptions={loadProductOptions}
                            className="w-full"
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            defaultOptions={true}
                            cacheOptions={true}
                            value={getSelectedProductOption(product.id, product.product)}
                            formatOptionLabel={customProductOptionLabel}
                            onChange={(selectedOption) => handleProductChange(index, selectedOption)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const selectInstance = productRefs.current[index];
                                const isMenuOpen = selectInstance?.state?.menuIsOpen;
                                const focusedOption = selectInstance?.state?.focusedOption;
                                
                                if (!isMenuOpen && !focusedOption && !product.product) {
                                  e.preventDefault();
                                  const nextIndex = index + 1;
                                  if (nextIndex < products.length && productRefs.current[nextIndex]) {
                                    productRefs.current[nextIndex].focus();
                                  } else if (index === products.length - 1) {
                                    submitbtnRef.current?.focus();
                                  }
                                }
                              }
                            }}
                            noOptionsMessage={({ inputValue }) => 
                              inputValue && inputValue.length < 2 
                                ? 'Type at least 2 characters to search' 
                                : 'No products found'
                            }
                            loadingMessage={() => 'Searching products...'}
                            required
                            ref={(el) => (productRefs.current[index] = el)}
                            styles={customSelectStyles}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '100px' }}>
                          <input
                            type="text"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                            value={product.batch || ''}
                            onChange={(e) => handleBatchChange(index, e.target.value)}
                            onKeyDown={(e) => handleBatchKeyDown(e, index)}
                            ref={(el) => (batchRefs.current[index] = el)}
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '100px' }}>
                          <input
                            type="text"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                            value={product.expiryDisplay || ''}
                            onChange={(e) => handleExpiryInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleExpiryKeyDown(e, index)}
                            ref={(el) => (expiryRefs.current[index] = el)}
                            placeholder="MM/YY"
                            maxLength={5}
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '100px' }}>
                          <input
                            type="text"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                            value={product.packing || ''}
                            onChange={(e) => handlePackingChange(index, e.target.value)}
                            onKeyDown={(e) => handlePackingKeyDown(e, index)}
                            ref={(el) => (packingRefs.current[index] = el)}
                            placeholder="10x10"
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '80px' }}>
                          <input
                            type="text"
                            step="0.01"
                            min="0"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                            value={product.mrp || ''}
                            onChange={(e) => handleMRPChange(index, e.target.value)}
                            onKeyDown={(e) => handleMRPKeyDown(e, index)}
                            ref={(el) => (mrpRefs.current[index] = el)}
                            placeholder="0.00"
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '80px' }}>
                          <input
                            type="text"
                            step="1"
                            min="0"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                            value={product.qty}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                            onKeyDown={(e) => handleQtyKeyDown(e, index)}
                            ref={(el) => (qtyRefs.current[index] = el)}
                            placeholder="1"
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '80px' }}>
                          <input
                            type="text"
                            step="0.01"
                            min="0"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                            value={product.rate || ''}
                            onChange={(e) => handleRateChange(index, e.target.value)}
                            onKeyDown={(e) => handleRateKeyDown(e, index)}
                            ref={(el) => (rateRefs.current[index] = el)}
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>


                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <input
                            type="text"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                            value={product.discount || ''}
                            onChange={(e) => handleDiscountChange(index, e.target.value)}
                            onKeyDown={(e) => handleDiscountKeyDown(e, index)}
                            ref={(el) => (discountRefs.current[index] = el)}
                            placeholder="10 or 10p"
                            title="Enter discount: 10 (fixed amount) or 10p (percentage)"
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '60px' }}>
                          <input
                            type="text"
                            step="0.01"
                            min="0"
                            max="100"
                            className="w-20 p-2.5 border border-gray-400 text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                            value={product.gstRate || ''}
                            onChange={(e) => handleTaxChange(index, e.target.value)}
                            onKeyDown={(e) => handleTaxKeyDown(e, index)}
                            ref={(el) => (taxRefs.current[index] = el)}
                            placeholder="12"
                            onFocus={(e) => e.target.select()}
                            disabled={!product.product}
                          />
                        </td>


                        <td className="px-3 py-2 whitespace-nowrap text-sm text-black font-bold text-right">
                          {product.totalAmount ? `₹${product.totalAmount}` : '-'}
                        </td>
                        
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            className="p-2 bg-red-50 text-red-500 rounded-lg hover:bg-red-100  transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRow(index);
                            }}
                            title="Remove product"
                          >
                            <MdDelete size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="15" className="px-3 py-6 text-center text-sm text-black-500">
                        No products added. Click the plus button to add a product.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-300">
              <button
                type="button"
                className="px-6 py-2 text-sm font-medium text-black-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors"
                onClick={resetForm}
              >
                Reset
              </button>
              <button
                type="button"
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-md hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSave}
                disabled={isButtonDisabled || loading}
                ref={submitbtnRef}
              >
                {loading ? 'Saving...' : 'Save Opening Stock'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddItemModal 
        showModal={showModal}
        setShowModal={setShowModal}
        onSave={handleItemSaved}
        productToEdit={null}
      />

    </div>
  );
};

export default withAuth(OpeningStockEntry);

